import React, { useEffect, useMemo } from "react";
import { Chip } from "@mui/material";
import { getContainedBy, getIndexForNode } from "../../../common";
import { useTranslation } from "react-i18next";
import { DecisionGraph } from "@imminently/decisively-core";
import Select, { type SelectOption, type SelectGroup } from "@imminently/decisively-ui/Select";
import { Label } from "@/components";
import { defaultGoalId } from "./goalMenuDefaultGoal";

export interface GoalMenuProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  ref?: React.Ref<any>;
  size?: "sm" | "md";
  graph?: any;
  onClick?: (id: string) => void;
  onGoalIdChange?: (id: string) => void;
  menuClassName?: string;
  allowEmpty?: boolean;
  /** a `placeholder` only makes sense if `allowEmpty` is `true` */
  placeholder?: string;
  goalId?: string;
  /** if `true` non instances (other than globals) will be ignored */
  instances?: boolean;
  disabled?: boolean;
  includeGoals?: boolean;
  includeInputs?: boolean; // Whether to show input nodes
  includeDerived?: boolean; // Whether to show derived nodes
  includeInferred?: boolean;
  includeIdentifiers?: boolean;
  includeMeta?: boolean;
  error?: boolean;
  required?: boolean;
  /** if present, should only show attributes that belong to this entity */
  entityName?: string;
  /** if true - ignore .hidden */
  showHidden?: boolean;
  /** if true - ignore .identifier */
  showIdentifiers?: boolean;
  label?: string;
  allowArbitrary?: boolean;
  "data-testid"?: string;
};

const GoalMenu = (props: GoalMenuProps) => {
  const {
    size,
    graph,
    onClick,
    onGoalIdChange,
    menuClassName,
    allowEmpty = false, /* not sure why */
    goalId: goalIdFromProps,
    instances = false,
    includeDerived = true,
    includeInputs = true,
    includeGoals = true,
    includeInferred = false,
    includeIdentifiers = false,
    includeMeta = false,
    entityName,
    showHidden,
    showIdentifiers,
    disabled,
    label,
    required,
    allowArbitrary,
    "data-testid": dataTestId,
    ref,
    error,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  const graphRef = React.useRef(graph);

  const [stateGoalId, setStateGoalId] = React.useState(goalIdFromProps);

  const currentGoalId = goalIdFromProps ?? stateGoalId;

  const handleGoalIdChange = (id: string) => {
    if (id !== stateGoalId) {
      setStateGoalId(id);
      if (onGoalIdChange) {
        onGoalIdChange(id);
      }
    }
  };

  const _graph: DecisionGraph | undefined = graph ? DecisionGraph.from(graph) : undefined; // Depends if the graph has already been generated

  const getChipLabel = (node: any) => {

    let indexInfo: any = getIndexForNode(node, graph) || {};
    let nodeIndex = "";
    if (indexInfo) {
      nodeIndex = indexInfo.value;
    }
    let cleanIndex = (nodeIndex && nodeIndex.length > 8) ? `${nodeIndex.substring(0, 8)}...` : nodeIndex;
    let contained = getContainedBy(node.path, graph);
    const idLabel = indexInfo.hint ? indexInfo.hint : nodeIndex;
    const entityName = node.entity;

    // NOTE: globals will never have the `cleanIndex` populated, but it's fine...no need for special conditions
    const chipLabel = `${entityName}${cleanIndex ? `: ${cleanIndex}` : ''}`;

    return (chipLabel);
  };


  useEffect(() => {

    if (graphRef.current !== graph) {
      graphRef.current = graph; // This used to be in the below code - but it caused additional goal resets.
    }

    if (_graph && !allowEmpty && currentGoalId === "") {
      const goalId = defaultGoalId(_graph, instances);
      if (goalId) handleGoalIdChange(goalId);
    }
  }, [currentGoalId, _graph, graph, allowEmpty]);

  useEffect(() => {
    if (!goalIdFromProps) return;


    /*
    // Why do we do this? This just unnecessarily resets the goal to the same id?
    const goal = _graph.node(goalIdFromProps);
    if (goal) {
      handleGoalIdChange(goal.id);
    }*/
  }, [goalIdFromProps]);

  const groups = useMemo(() => {
    const result: any = [];
    if (includeGoals) result.push({
      name: t('debugger.goals'),
      filter: (i: any) => i.type === 'goal',
    });
    if (includeInputs) result.push({
      name: t('debugger.inputs'),
      filter: (i: any) => i.type === 'input'
    });
    if (includeDerived) result.push({
      name: t('debugger.derived'),
      filter: (i: any) => i.type === 'intermediate',
    });
    if (includeInferred) result.push({
      name: t('debugger.inferred'),
      filter: (i: any) => i.type === 'inferred',
    });
    if (includeIdentifiers) result.push({
      name: t('debugger.identifier'),
      filter: (i: any) => i.type === 'identifier',
    });
    if (includeMeta) result.push({
      name: "Meta",
      filter: (i: any) => i.type === 'meta'
    });
    return result;
  }, [includeGoals, includeInputs, includeDerived, includeInferred, includeIdentifiers, includeMeta, t]);

  const selectOptions = useMemo(() => {
    const allNodes: any[] = [];
    if (_graph) {
      for (const n of _graph.ids()) {
        // filter on single fields before making the node object
        const fk = _graph.field(n, "fk");
        const identifier = _graph.field(n, "identifier");
        if (fk || (identifier && !showIdentifiers)) continue;

        // why over complicate it?...just check hidden
        if (instances && _graph.field(n, "hidden") && !showHidden) continue;

        if (entityName && _graph.field(n, "entity") !== entityName) continue;

        const node: any = _graph.node(n);
        if (!node) continue;

        let type = 'goal';

        if (node.meta) type = 'meta';
        else if (node.inferred || node.fk) type = 'inferred';
        else if (node.identifier) type = 'identifier';
        else if (_graph.predecessors(n).length > 0) {
          if (_graph.successors(n).length > 0) type = 'intermediate';
          else type = 'input';
        }

        const nodeId = instances ? (node.path || node.id) : node.id;
        allNodes.push({
          id: node.id,
          name: node.description,
          chipLabel: getChipLabel(node),
          type: type,
          path: node.path,
          entity: node.entity,
          value: nodeId,
          label: node.description || node.id,
        });
      }
    }

    // Group options
    const groupedOptions: SelectGroup[] = [];
    groups.forEach(group => {
      const groupNodes = allNodes.filter(group.filter);
      if (groupNodes.length > 0) {
        groupedOptions.push({
          label: group.name,
          options: groupNodes.map(node => ({
            value: node.value,
            label: node.label,
          })),
        });
      }
    });

    return groupedOptions;
  }, [_graph, showIdentifiers, instances, showHidden, entityName, groups]);

  const handleSelectChange = (value: string | null) => {
    if (value) {
      handleGoalIdChange(value);
    } else if (allowEmpty) {
      handleGoalIdChange("");
    }
  };

  return (
    <div
      style={{
        display: "block",
        position: "relative",
      }}
      {...otherProps}
    >
      {label ? <Label>{label}</Label> : null}
      <div style={{ position: "relative" }}>
        <Select
          size={size}
          error={error}
          ref={ref}
          data-testid={dataTestId}
          value={currentGoalId || null}
          onValueChange={handleSelectChange}
          options={selectOptions}
          allowArbitrary={allowArbitrary}
          placeholder={allowEmpty ? (props.placeholder || t('build.search_attributes_placeholder')) : undefined}
          disabled={disabled || !graph}
          clearable={Boolean(currentGoalId && currentGoalId !== "" && allowEmpty && !disabled)}
          className={menuClassName}
        />
      </div>
    </div>
  );
};

export default React.memo(GoalMenu);
